import { Dialog, notifyError, notifySuccess } from '@/components'
import UnitSelect from '@/modules/add-new-unit/components/UnitSelect/UnitSelect'
import {
  useAddNewDitUnitMutation,
  useAddNewLightningUnitMutation,
} from '@/modules/add-new-unit/services/newUnitService'
import Input from '@/modules/organization/components/Input/Input'
import { UserTypes } from '@/types/typeUser'
import TimePicker from '@/modules/aqua-device-module/components/actions/ui/TimePicker'
import UploadFeaturesDoc from '@/components/Organisms/UploadFeaturesDoc/UploadFeaturesDoc'
import UploadFeaturesDocList from '@/components/Organisms/UploadFeaturesDoc/UploadFeaturesDocList'
import DeleteDeviceButton from '@/modules/device-module/components/deleteDeviceButton/deleteDeviceButton'
import UploadFeaturesImg from '@/components/Organisms/UploadFeaturesImg/UploadFeaturesImg'
import { useChangeSet } from '@/utils/useChangeSet'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { LoadingButton } from '@mui/lab'
import { Box, Typography, InputAdornment, Link, Stack } from '@mui/material'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'
import MapLightnings from '@/components/Organisms/Map/MapLightnings'
import { AddNewLightningUnitReq } from '@/modules/add-new-unit/types/newUnitType'
import CustomSelect from '@/components/CustomSelect/CustomSelect'
import { systemStatusSelect } from '@/modules/add-new-tsodd/components/UnitData/UnitData'

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Название не может быть пустым')
    .trim('Название не может быть пустым')
    .max(30, 'Поле должно содержать не более 30 символов'),
  lightingLength: Yup.number()
    .required('Укажите протяженность')
    .typeError('')
    .test('isInt', '', (value) => {
      if (!value) return true
      return Number.isInteger(value)
    }),
  lightingPoles: Yup.number()
    .required('Укажите кол-во опор')
    .typeError('')
    .test('isInt', '', (value) => {
      if (!value) return true
      return Number.isInteger(value)
    }),
  lightingLuminaires: Yup.number()
    .required('Укажите кол-во светильников')
    .typeError('')
    .test('isInt', '', (value) => {
      if (!value) return true
      return Number.isInteger(value)
    }),
  lightingPower: Yup.number()
    .required('Укажите мощность')
    .typeError('')
    .test('maxDecimals', '', (value) => {
      if (!value) return true
      const decimalPart = value.toString().split('.')[1]
      return !decimalPart || decimalPart.length <= 10
    }),
  address: Yup.string()
    .required('Адрес не может быть пустым')
    .trim('Адрес не может быть пустым')
    .max(250, 'Поле должно содержать не более 250 символов'),
})

export interface IDataFields {
  name: string
  address: string
  typeId: number
  lightingImageUrl: string | null
  lightingLength: number | ''
  lightingPoles: number | ''
  lightingLuminaires: number | ''
  lightingPower: number | ''
  coordinates: number[][]
}

interface IUnitsFields {
  name: string
  address: string
  typeId: number
  lightingLength: number | ''
  lightingPoles: number | ''
  lightingLuminaires: number | ''
  lightingPower: number | ''
}

type Props = {
  formValues: any
  units: any
  refetch: () => void
  currentUserType: number
  documents: any
}
const FeaturesLightningForm: React.FC<Props> = ({
  formValues,
  currentUserType,
  refetch,
  documents,
  units,
}) => {
  const {
    name,
    address,
    coordinates,
    lightingLength,
    lightingLuminaires,
    lightingPoles,
    lightingPower,
    typeId,
    lightingImageUrl,
  } = formValues
  const getLightsLocations = units?.data?.data.filter((item: any) => {
    return item.typeId === 14
  })
  const parsedCoordinates = JSON.parse(formValues?.coordinates) || []
  const [addLightningUnitService] = useAddNewLightningUnitMutation()
  const { id } = useParams()
  const [lightStatus, setLightStatus] = useState<1 | 2>(
    formValues?.lightingTypeId || 1
  )
  const [openDialogSave, setOpenDialogSave] = useState(false)
  const [points, setPoints] = useState<number[][]>(parsedCoordinates)
  const [formChanged, setFormChanged] = useState(false)
  const defaultValues: IDataFields = {
    name,
    address,
    typeId,
    lightingImageUrl: '',
    lightingLength,
    lightingPoles,
    lightingLuminaires,
    lightingPower,
    coordinates: [],
  }
  const { handleSubmit, control, trigger, formState, watch } =
    useForm<IDataFields>({
      defaultValues,
      resolver: yupResolver(validationSchema),
      criteriaMode: 'all',
      reValidateMode: 'onChange',
    })

  const onSaveBtnClick = () => {
    setOpenDialogSave(true)
  }

  useChangeSet(() => {
    trigger().then((hasErrors: boolean) => {
      if (hasErrors) {
        setFormChanged(true)
      } else {
        setFormChanged(false)
      }
    })
  }, [
    watch('name'),
    watch('address'),
    watch('lightingLength'),
    watch('lightingPower'),
    watch('lightingPoles'),
    watch('lightingLuminaires'),
  ])

  const onSubmit = async (dataForm: IUnitsFields) => {
    try {
      setOpenDialogSave(false)

      // Parse lightingPower as string first to preserve decimal values
      let lightingPowerValue = 0
      if (dataForm?.lightingPower !== '') {
        if (typeof dataForm?.lightingPower === 'string') {
          lightingPowerValue = parseFloat(dataForm.lightingPower)
        } else {
          lightingPowerValue = dataForm.lightingPower
        }
      }

      const newUnit: AddNewLightningUnitReq = {
        id: Number(id),
        name: dataForm.name,
        address: dataForm.address,
        typeId: typeId,
        coordinates: JSON.stringify(points),
        lightingLuminaires: Number(dataForm.lightingLuminaires) | 0,
        lightingLength: Number(dataForm?.lightingLength) | 0,
        lightingPoles: Number(dataForm?.lightingPoles) | 0,
        lightingPower: lightingPowerValue,
        lightingTypeId: lightStatus,
      }
      const res = await addLightningUnitService(newUnit).unwrap()
      if (res.success) {
        notifySuccess('Данные успешно сохранены')
        refetch()
      }
    } catch (error) {
      console.log(error)
    }
  }

  const isUser = currentUserType === UserTypes.user

  const defaultCenter = points.length ? points[0] : [47.2313, 39.7233]
  return (
    <>
      <Dialog
        message='Сохранить внесенные изменения?'
        open={openDialogSave}
        handleClose={() => setOpenDialogSave(false)}
        handleAgree={() => {
          if (Object.keys(formState.errors).length >= 1) {
            notifyError('Исправте ошибки в форме')
          }
          handleSubmit(onSubmit)()
        }}
      />

      <Box
        flex={1}
        display='flex'
        flexDirection={{ xss: 'column', md: 'row' }}
      >
        <Box
          flex={1}
          display='flex'
          border='1px solid #0072BC'
          mt={{ xss: 0, lg: '35px', xlgx: '-48px' }}
          height={{
            mdx: '100%',
            lg: 'calc(100% - 35px)',
            xlgx: 'calc(100% + 48px)',
          }}
        >
          <MapLightnings
            disabled={isUser}
            typeId={lightStatus}
            defaultCenter={defaultCenter}
            defaultZoom={13}
            initialCoordinates={points}
            onCoordinatesChange={(coords) => {
              setFormChanged(true)
              setPoints(coords)
            }}
            staticPolylines={getLightsLocations}
            excludeId={Number(id)}
          />
        </Box>
        <Box
          maxWidth={{ xss: '100%', md: 550 }}
          maxHeight={'calc(100vh - 245px)'}
          overflow={'auto'}
          display='flex'
          flex={{ xss: 1, xl4: 0.7, xll: 0.53 }}
        >
          <form
            style={{ width: '100%' }}
            onSubmit={(e) => e.preventDefault()}
          >
            <Box
              display='flex'
              flex={1}
              flexDirection={{ xss: 'column', md: 'column' }}
              pb={{ xss: 0.5, lg: 1 }}
            >
              <Box
                display='flex'
                flexDirection={{ xss: 'column', xlg: 'row' }}
              >
                <Box
                  flex={1}
                  px={1}
                >
                  <Typography
                    variant='h6'
                    fontSize={18}
                    color='primary'
                  >
                    Информация о системе
                  </Typography>
                  <Box
                    display='flex'
                    flexDirection={'column'}
                    gap={1.5}
                  >
                    <Box
                      display='flex'
                      gap={2}
                      flexWrap={{ xss: 'wrap', lg: 'nowrap' }}
                    >
                      <CustomSelect
                        disabled={true}
                        items={[{ id: 1, name: 'Освещение' }]}
                        defaultValue={1}
                        label='Выберите систему'
                      />
                      <CustomSelect
                        items={systemStatusSelect}
                        defaultValue={lightStatus}
                        label='Выберите статус освещения'
                        onChange={(e) => {
                          // @ts-ignore
                          setLightStatus(e?.target?.value)
                          setFormChanged(true)
                        }}
                        disabled={isUser}
                      />
                    </Box>
                    <Input
                      name='name'
                      type='text'
                      label='Имя'
                      control={control}
                      disabled={isUser}
                    />
                    <Input
                      name='address'
                      type='text'
                      label='Адрес'
                      control={control}
                      disabled={isUser}
                    />
                    <Box
                      flex={1}
                      gap={1}
                      display='flex'
                      flexDirection={{ xss: 'column', md: 'row' }}
                    >
                      <Input
                        name='lightingLength'
                        type='number'
                        label='Протяженность'
                        control={control}
                        disabled={isUser}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>м</InputAdornment>
                          ),
                        }}
                      />
                      <Input
                        name='lightingPoles'
                        type='number'
                        label='Опоры'
                        control={control}
                        disabled={isUser}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>шт</InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    <Box
                      flex={1}
                      gap={1}
                      display='flex'
                      flexDirection={{ xss: 'column', md: 'row' }}
                    >
                      <Input
                        name='lightingLuminaires'
                        type='number'
                        label='Светильники'
                        control={control}
                        disabled={isUser}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>шт</InputAdornment>
                          ),
                        }}
                      />
                      <Input
                        name='lightingPower'
                        type='number'
                        label='Мощность'
                        control={control}
                        disabled={isUser}
                        allowDecimal={true}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>кВт</InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
              {!isUser && (
                <>
                  <UploadFeaturesImg url={lightingImageUrl} />
                  <UploadFeaturesDocList documents={documents} />
                </>
              )}
              {!isUser && (
                <Box
                  display='flex'
                  alignItems='flex-start'
                  justifyContent='flex-end'
                  gap={2}
                  mr={{ xss: 0.5, lg: 2 }}
                  pt={1}
                >
                  <UploadFeaturesDoc documents={documents} />
                  {currentUserType !== UserTypes.superAdmin ? null : (
                    <>{id && <DeleteDeviceButton deviceId={id} />}</>
                  )}
                  <LoadingButton
                    variant='outlined'
                    onClick={onSaveBtnClick}
                    disabled={!formChanged}
                  >
                    <span>Сохранить</span>
                  </LoadingButton>
                </Box>
              )}
            </Box>
          </form>
        </Box>
      </Box>
    </>
  )
}

export default FeaturesLightningForm
